import { UseFormReturn } from "react-hook-form";
import { InquiryFormData, useInquiryConstants } from "../../../../hooks/use-inquiries";
import { FC, useEffect, useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import { InputField } from "../../../forms/input-field";
import { SelectField } from "../../../forms/select-field";
import { useMyProfile } from "../../../../hooks/use-my-profile";
import moment from "moment";
import { useCenterConstants } from "../../../../hooks/use-center";
import { useAccessControl } from "../../../../app/use-access-control";
import { CODES } from "../../../../data/dynamics-codes";

export interface InitialContactProps {
  inquiryForm: UseFormReturn<InquiryFormData>;
  isDetailsTab?: boolean;
}

export const InitialContact: FC<InitialContactProps> = ({
  inquiryForm: { watch, register, getValues, formState, setValue },
  isDetailsTab
}) => {
  const { profile } = useMyProfile();
  const inquiryConstantsQuery = useInquiryConstants();
  const { activeCenterId } = useAccessControl();

  const {
    activeConstants: { cecEnrollmentStatus }
  } = useCenterConstants(activeCenterId);

  const referredById = watch("referredById");
  const referredByDescriptionLabel = useMemo(() => {
    const selection = inquiryConstantsQuery.data?.activeReferralSources?.find(afs => afs.value === referredById);
    if (selection) {
      return `Referred by ${selection.name}`;
    } else {
      return "";
    }
  }, [referredById, inquiryConstantsQuery.data?.activeReferralSources]);

  const sourceId = watch("sourceId");
  const showCecCheckbox = useMemo(() => {
    if (!sourceId) {
      return cecEnrollmentStatus;
    }

    const selection = inquiryConstantsQuery.data?.contactMethodSources?.find(cms => cms.value === sourceId);
    const isCecManagedOption = selection?.cecManagedOption ?? false;
    return cecEnrollmentStatus && isCecManagedOption;
  }, [cecEnrollmentStatus, sourceId, inquiryConstantsQuery.data?.contactMethodSources]);

  useEffect(() => {
    setValue("cecManaged", !!showCecCheckbox);
  }, [showCecCheckbox, setValue]);

  return (
    <Row>
      <Col md={6}>
        <InputField
          disableColClass
          type="date"
          label="Inquiry Date"
          validationState={!isDetailsTab && formState?.errors["inquiryDate"] ? "error" : undefined}
          disabled={isDetailsTab}
          max={moment().format("YYYY-MM-DD")}
          min={moment().subtract(180, "days").format("YYYY-MM-DD")}
          {...register("inquiryDate")}
        />
      </Col>
      <Col md={6}>
        <InputField
          disableColClass
          type="time"
          label="Time Received"
          {...register("inquiryTimeReceived")}
          disabled={isDetailsTab}
        />
      </Col>
      <Col md={6}>
        {isDetailsTab && (
          <InputField
            disableColClass
            label="Taken By"
            value={`${getValues("takenBy.surname")}, ${getValues("takenBy.givenName")}`}
            disabled
          />
        )}
        {/* TODO: update with the appropriate drop down logic */}
        {!isDetailsTab && (
          <SelectField disableColClass label="Taken By" {...register("takenById")}>
            <option value={profile?.id}>{profile?.displayName}</option>
          </SelectField>
        )}
      </Col>
      <Col md={6}>
        <SelectField
          disableColClass
          label="Inquiry Method"
          defaultValue=""
          isRequired={!isDetailsTab}
          validationState={formState?.errors.sourceId ? "error" : undefined}
          disabled={isDetailsTab}
          {...register("sourceId")}
        >
          <option value="">Select Method</option>
          {inquiryConstantsQuery.data?.contactMethodSources
            .filter(method => cecEnrollmentStatus || method.internalCode !== CODES.METHODS.CEC_VOICEMAIL)
            .map(({ name, value }) => (
              <option key={value} value={value}>
                {name}
              </option>
            ))}
        </SelectField>
      </Col>
      {!isDetailsTab && showCecCheckbox && (
        <Col md={12}>
          <div className="form-check">
            <label className="form-check-label" htmlFor="CEC">
              CEC Managed Inquiry
            </label>
            <input className="form-check-input" type="checkbox" id="CEC" {...register("cecManaged")} />
          </div>
        </Col>
      )}
      <Col md={isDetailsTab ? 6 : 12}>
        <SelectField
          data-testid="inquiry-referred-by-dropdown"
          disableColClass
          label="Referred By"
          defaultValue=""
          isRequired={!isDetailsTab}
          validationState={formState?.errors.referredById ? "error" : undefined}
          disabled={isDetailsTab}
          {...register("referredById")}
        >
          <option disabled value="" />
          {inquiryConstantsQuery.data?.activeReferralSources.map(({ name, value }) => (
            <option key={value} value={value}>
              {name}
            </option>
          ))}
        </SelectField>
      </Col>

      {!!referredByDescriptionLabel && (
        <Col md={isDetailsTab ? 6 : 12}>
          <InputField
            data-testid="inquiry-referred-by-description"
            type="text"
            label={referredByDescriptionLabel}
            disabled={isDetailsTab}
            {...register("referredByOther")}
          />
        </Col>
      )}
    </Row>
  );
};
